<script setup>

    import { thumbailForSpeaker } from '@/composables/helpers/speakers';

    const defaultSpeakerImg = '/assets/img/placeholder-speaker.jpg';

    const props = defineProps({
        speaker: {
            type: Object,
            required: true
        },
        hide_bio: {
            type: Boolean,
            default: false
        },
        hide_featured: {
            type: Boolean,
            default: false
        }
    })

    const bio = computed(() => {
        if (!props.speaker.bio) return '';
        const plain = String(props.speaker.bio).replace(/<[^>]*>/g, '');
        if (plain.length <= 320) return plain;
        return plain.slice(0, 320).trimEnd() + '…';
    });

    // If the speaker is associated with a sponsor/company that has a logo,
    // render a small chip in the bottom-right of the hex portrait. Featured
    // speakers from Sessionboard publish their company logo via the
    // `featured_speaker_logo` field, which we also fall back to.
    const companyLogo = computed(() => {
        const fromSponsor = props.speaker?.sponsor?.sponsor_logo?.[0];
        const fromField = props.speaker?.company_logo?.[0];
        const fromFeatured = props.speaker?.featured_speaker_logo?.[0];
        return fromSponsor || fromField || fromFeatured || null;
    });

    const speakerHref = computed(() => {
        if (!props.speaker?.id && !props.speaker?.slug) return null;
        return `/speakers/${props.speaker.slug || props.speaker.id}`;
    });

    const speakerImage = computed(() => thumbailForSpeaker(props.speaker));

</script>

<template>
    <div class="speaker-card flex flex-col gap-[28px] items-start shrink-0
        w-[280px] lg:w-auto
        lg:flex-row lg:items-center lg:gap-[35px]">

        <div class="speaker-hex-container relative w-[202px] h-[202px] shrink-0">
            <MessLink v-if="speakerHref"
                :href="speakerHref"
                class="speaker-hex-link group relative display-block w-full h-full overflow-hidden">
                <img :src="speakerImage?.url || defaultSpeakerImg"
                    :alt="`${speaker.name} profile photo`"
                    loading="lazy"
                    width="202" height="202"
                    class="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110" />
            </MessLink>
            <div v-else
                class="speaker-hex-link group relative display-block w-full h-full overflow-hidden">
                <img :src="speakerImage?.url || defaultSpeakerImg"
                    :alt="`${speaker.name} profile photo`"
                    loading="lazy"
                    width="202" height="202"
                    class="w-full h-full object-cover" />
            </div>

            <div v-if="companyLogo"
                class="company-chip absolute bottom-[3px] right-[10px] w-[84px] h-[44px] bg-white rounded-[50px] overflow-hidden flex items-center justify-center pointer-events-none">
                <img :src="companyLogo.url"
                    :alt="speaker.company || 'Company logo'"
                    loading="lazy"
                    class="max-w-[60px] max-h-[32px] object-contain" />
            </div>
        </div>

        <div class="flex flex-col gap-[16px] items-start text-[var(--magenta)] w-full lg:w-[340px]">
            <MessLink v-if="speakerHref"
                :href="speakerHref"
                class="speaker-name font-hubspot-serif font-normal text-[30px] leading-[1.2] m-0 no-underline text-[var(--magenta)]
                    decoration-dotted decoration-[var(--dark-pink)] decoration-[3.5%] underline-offset-[4.5px]
                    [text-decoration-skip-ink:auto] [text-underline-position:from-font]
                    hover:underline">
                <span v-html="speaker.name" />
            </MessLink>
            <h3 v-else
                class="speaker-name font-hubspot-serif font-normal text-[30px] leading-[1.2] m-0 text-[var(--magenta)]">
                <span v-html="speaker.name" />
            </h3>

            <p v-if="speaker.job_title || speaker.company"
                class="font-hubspot-sans font-medium text-[12px] leading-[1.5] tracking-[0.84px] uppercase m-0">
                <span v-if="speaker.job_title" v-html="speaker.job_title"></span>
                <span v-if="speaker.company">&nbsp;@ {{ speaker.company }}</span>
            </p>

            <MessLink v-if="!hide_bio && speaker.bio && speakerHref"
                :href="speakerHref"
                class="speaker-bio font-hubspot-sans font-light text-[15px] leading-[23px] tracking-[0.3px] m-0 line-clamp-3 no-underline pb-[2px]
                    text-[var(--magenta)]
                    decoration-dotted decoration-[var(--dark-pink)] decoration-[1px] underline-offset-[3px]
                    [text-decoration-skip-ink:auto] [text-underline-position:from-font]
                    hover:underline">
                {{ bio }}
            </MessLink>
            <div v-else-if="!hide_bio && speaker.bio"
                class="speaker-bio font-hubspot-sans font-light text-[15px] leading-[23px] tracking-[0.3px] m-0 line-clamp-3">
                {{ bio }}
            </div>
        </div>
    </div>
</template>

<style>
.speaker-card .speaker-hex-link {
    mask-image: url('/img/2026/speakers/speaker-hex.svg');
    -webkit-mask-image: url('/img/2026/speakers/speaker-hex.svg');
    mask-size: 100% 100%;
    -webkit-mask-size: 100% 100%;
    mask-repeat: no-repeat;
    -webkit-mask-repeat: no-repeat;
}
</style>
